import React from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { TablePaginationConfig } from "antd/es/table";
import styles from "./TablePagination.module.scss";

export const DEFAULT_TABLE_PAGE_SIZE = 10;
export const DEFAULT_TABLE_PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

export type TablePaginationFormat = "slash" | "i18n";

export interface TablePaginationOptions {
  current: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
  pageSize?: number;
  pageSizeOptions?: string[];
  /** Current page row count; used while total is still loading. */
  dataLength?: number;
  /** When true, shows a spinner in the total label; hides page numbers until total is known. */
  countLoading?: boolean;
  showSizeChanger?: boolean;
  format?: TablePaginationFormat;
}

/**
 * Standard Ant Design Table pagination config for list pages.
 * Use countLoading when total count is fetched separately from page rows.
 */
export function getTablePagination(
  t: (key: string, options?: Record<string, unknown>) => string,
  options: TablePaginationOptions
): TablePaginationConfig {
  const {
    current,
    total,
    onChange,
    pageSize = DEFAULT_TABLE_PAGE_SIZE,
    pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
    dataLength = 0,
    countLoading = false,
    showSizeChanger = false,
    format = "slash",
  } = options;

  // While count is loading, only allow prev/next (no fake page 2, 3…).
  // Enable "next" only when the current page is full.
  const resolvedTotal = countLoading
    ? (current - 1) * pageSize + dataLength + (dataLength >= pageSize ? 1 : 0)
    : total;

  const paginationClassName = [
    showSizeChanger ? styles.sizeBelow : undefined,
    countLoading ? styles.countLoading : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return {
    current,
    pageSize,
    total: resolvedTotal,
    showSizeChanger,
    pageSizeOptions,
    className: paginationClassName || undefined,
    onChange,
    itemRender: (page, type, element) => {
      if (!countLoading) {
        return element;
      }
      if (type === "jump-prev" || type === "jump-next") {
        return null;
      }
      if (type === "page" && page !== current) {
        return null;
      }
      return element;
    },
    locale: showSizeChanger
      ? {
          items_per_page: t("Common-ItemsPerPage"),
        }
      : undefined,
    showTotal: (_totalValue, range) => {
      if (countLoading) {
        return (
          <span>
            {range[0]}-{range[1]} / <LoadingOutlined spin />
          </span>
        );
      }

      if (format === "i18n") {
        return t("Common-PaginationInfo", {
          from: range[0],
          to: range[1],
          total,
        });
      }

      return `${range[0]}-${range[1]} / ${total}`;
    },
  };
}
